import { cn } from "@/lib/utils";

interface StepCardProps {
  number: string;
  title: string;
  description: string;
  variant: "purple" | "green" | "blue";
  iconPosition?: string;
}

function StepCard({
  number,
  title,
  description,
  variant,
  iconPosition = "-12px",
}: StepCardProps) {
  const styles = {
    purple: {
      bg: "#b981f2",
      numberBg: "bg-white",
      numberColor: "text-[#b981f2]",
      titleColor: "text-[#ebebff]",
      descriptionColor: "text-[#ebebff]",
    },
    green: {
      bg: "rgba(70,208,125,.15)",
      numberBg: "bg-white",
      numberColor: "text-[#28c968]",
      titleColor: "text-[#28c968]",
      descriptionColor: "text-[#58886d]",
    },
    blue: {
      bg: "#cdf9fe",
      numberBg: "bg-white",
      numberColor: "text-[#449099]",
      titleColor: "text-[#449099]",
      descriptionColor: "text-[#449099]",
    },
  };

  const style = styles[variant];

  const StepSvg = {
    purple: StepOneSvg,
    green: StepTwoSvg,
    blue: StepThreeSvg,
  }[variant];

  return (
    <div
      className="relative p-6 rounded-xl md:h-[226px] h-full overflow-hidden md:w-[305px] w-full"
      style={{ backgroundColor: style.bg }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className={cn(
            "w-8 h-8 rounded-full flex items-center justify-center shadow-sm",
            style.numberBg
          )}
        >
          <span className={cn("text-lg font-medium", style.numberColor)}>
            {number}
          </span>
        </div>
        <h3 className={cn("text-lg font-medium", style.titleColor)}>{title}</h3>
      </div>
      <div className="relative">
        <p
          className={cn(
            "text-[14px] leading-relaxed max-w-[75%]",
            style.descriptionColor
          )}
        >
          {description}
        </p>
      </div>
      <div
        className="absolute top-1/2 -translate-y-1/2"
        style={{ right: iconPosition }}
      >
        <StepSvg />
      </div>
    </div>
  );
}

const StepOneSvg = () => (
  <svg
    width="99"
    height="103"
    viewBox="0 0 99 103"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g opacity="0.3" filter="url(#filter0_d_355_3776)">
      <rect
        width="67.9002"
        height="92.0866"
        rx="19.0344"
        transform="matrix(0.999971 0.00759392 -0.0122878 0.999924 26.3621 2.95264)"
        fill="#6B399E"
      ></rect>
    </g>
    <g filter="url(#filter1_d_355_3776)">
      <rect
        width="63.1357"
        height="68.9139"
        rx="19.0344"
        transform="matrix(0.999971 0.00759392 -0.0122878 0.999924 6.38684 14.4072)"
        fill="white"
      ></rect>
    </g>
    <rect
      opacity="0.3"
      x="14.963"
      y="62.146"
      width="43.3441"
      height="8.48037"
      rx="4.24018"
      fill="#B981F2"
    ></rect>
    <circle
      opacity="0.3"
      cx="36.6349"
      cy="37.6474"
      r="10.3649"
      fill="#EAD9FB"
    ></circle>
    <g filter="url(#filter2_d_355_3776)">
      <path
        d="M67.432 60.0271C67.432 60.0271 67.4321 60.0272 67.4321 60.0271C67.6447 59.7381 67.7434 59.4309 67.7281 59.1059C67.7131 58.7687 67.5839 58.4615 67.3407 58.1845C67.0975 57.9196 66.7861 57.7268 66.4062 57.6064L43.3143 50.3623C42.9192 50.2418 42.5165 50.2116 42.1061 50.2718C41.6804 50.3441 41.3235 50.4946 41.0347 50.7234C40.7307 50.9643 40.5333 51.2534 40.442 51.5906C40.3661 51.9157 40.404 52.235 40.5559 52.5481L49.6969 70.8661C49.8488 71.167 50.0921 71.414 50.4263 71.6065C50.7759 71.7993 51.1557 71.9017 51.5661 71.9137H51.6572C52.0676 71.9137 52.4477 71.8293 52.797 71.6607C53.1315 71.4921 53.3898 71.2693 53.5721 70.9924L54.5223 69.4624C56.6878 65.9755 59.9224 63.2819 63.7438 61.7836L66.5657 60.6771C66.9302 60.5327 67.2189 60.3158 67.4319 60.027C67.4319 60.027 67.432 60.027 67.432 60.0271V60.0271Z"
        fill="white"
      ></path>
      <path
        d="M68.1729 60.6095C67.8478 61.0409 67.417 61.3533 66.9127 61.5531L66.9097 61.5543L64.0877 62.6608C60.4556 64.085 57.3811 66.6452 55.3228 69.9595L54.3726 71.4895L54.366 71.5001L54.3591 71.5106C54.075 71.942 53.6836 72.2691 53.221 72.5022L53.2138 72.5058L53.2066 72.5093C52.7223 72.7431 52.2011 72.856 51.6572 72.856H51.5661H51.5522L51.5384 72.8556C50.9828 72.8392 50.4559 72.6989 49.9713 72.4316L49.9636 72.4274L49.956 72.423C49.4861 72.1523 49.1019 71.7782 48.8558 71.2906L48.8538 71.2868L39.7128 52.9688L39.7081 52.9594L39.7082 52.9594C39.4662 52.4607 39.3973 51.9211 39.5243 51.3766L39.5281 51.3603L39.5325 51.3442C39.6822 50.7915 40.0055 50.3368 40.4495 49.985L68.1729 60.6095ZM68.1729 60.6095C68.179 60.6016 68.1851 60.5936 68.1912 60.5854C68.5174 60.142 68.6953 59.6263 68.6695 59.064C68.644 58.4923 68.4194 57.9849 68.0487 57.5627L68.0418 57.5549L68.0347 57.5472C67.6687 57.1486 67.2107 56.873 66.691 56.7082L66.6882 56.7073L43.5963 49.4632L43.5963 49.4632L43.5891 49.461C43.0604 49.2997 42.517 49.2591 41.9692 49.3395L41.9587 49.3411L41.9482 49.3428C41.3934 49.4371 40.8822 49.642 40.4495 49.9849L68.1729 60.6095ZM68.1061 60.6862C67.855 60.9475 67.5045 61.0193 67.194 60.9387C67.5062 61.0172 67.858 60.9423 68.1061 60.6862ZM67.7291 59.1324C68.0627 59.2389 68.3384 59.525 68.3715 59.9548C68.339 59.5328 68.0635 59.2424 67.7291 59.1327C67.7291 59.1326 67.7291 59.1325 67.7291 59.1324Z"
        stroke="#C48EFC"
        stroke-width="1.88453"
      ></path>
    </g>
    <defs>
      <filter
        id="filter0_d_355_3776"
        x="20.6764"
        y="0.703447"
        width="78.1379"
        height="101.88"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix"></feFlood>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        ></feColorMatrix>
        <feOffset dy="2.39323"></feOffset>
        <feGaussianBlur stdDeviation="2.39323"></feGaussianBlur>
        <feComposite in2="hardAlpha" operator="out"></feComposite>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_355_3776"
        ></feBlend>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_355_3776"
          result="shape"
        ></feBlend>
      </filter>
      <filter
        id="filter1_d_355_3776"
        x="0.985996"
        y="12.158"
        width="73.0888"
        height="78.673"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix"></feFlood>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        ></feColorMatrix>
        <feOffset dy="2.39323"></feOffset>
        <feGaussianBlur stdDeviation="2.39323"></feGaussianBlur>
        <feComposite in2="hardAlpha" operator="out"></feComposite>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_355_3776"
        ></feBlend>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_355_3776"
          result="shape"
        ></feBlend>
      </filter>
      <filter
        id="filter2_d_355_3776"
        x="33.7331"
        y="48.3572"
        width="40.6677"
        height="36.8235"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix"></feFlood>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        ></feColorMatrix>
        <feOffset dy="6.59584"></feOffset>
        <feGaussianBlur stdDeviation="2.39323"></feGaussianBlur>
        <feComposite in2="hardAlpha" operator="out"></feComposite>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_355_3776"
        ></feBlend>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_355_3776"
          result="shape"
        ></feBlend>
      </filter>
    </defs>
  </svg>
);

const StepTwoSvg = () => (
  <svg
    width="80"
    height="111"
    viewBox="0 0 80 111"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g filter="url(#filter0_d_42_1241)">
      <rect
        width="104.106"
        height="29.2168"
        rx="14.6084"
        transform="matrix(0.999971 0.00759392 -0.0122878 0.999924 5.14429 38.0232)"
        fill="white"
      ></rect>
    </g>
    <g opacity="0.2" filter="url(#filter1_d_42_1241)">
      <rect
        width="104.106"
        height="29.2168"
        rx="14.6084"
        transform="matrix(0.999971 0.00759392 -0.0122878 0.999924 17.8484 73.8291)"
        fill="#6FC57D"
      ></rect>
    </g>
    <g opacity="0.2" filter="url(#filter2_d_42_1241)">
      <rect
        width="104.106"
        height="29.2168"
        rx="14.6084"
        transform="matrix(0.999971 0.00759392 -0.0122878 0.999924 17.8483 3.01221)"
        fill="#6FC57D"
      ></rect>
    </g>
    <rect
      opacity="0.3"
      x="17.4893"
      y="48.241"
      width="48.9977"
      height="5.65358"
      rx="2.82679"
      fill="#91F281"
    ></rect>
    <g filter="url(#filter3_dd_42_1241)">
      <path
        d="M66.1892 75.9292C66.1892 75.9292 66.1893 75.9293 66.1893 75.9292C66.4019 75.6402 66.5006 75.333 66.4853 75.008C66.4703 74.6708 66.3411 74.3636 66.0979 74.0866C65.8547 73.8217 65.5433 73.6289 65.1634 73.5085L42.0715 66.2644C41.6764 66.1439 41.2737 66.1137 40.8633 66.1739C40.4376 66.2462 40.0807 66.3967 39.7919 66.6255C39.4879 66.8664 39.2905 67.1555 39.1992 67.4927C39.1233 67.8178 39.1612 68.1371 39.3131 68.4502L48.4541 86.7682C48.606 87.0691 48.8493 87.3161 49.1835 87.5086C49.5331 87.7014 49.9129 87.8038 50.3233 87.8158H50.4144C50.8248 87.8158 51.2049 87.7314 51.5542 87.5628C51.8887 87.3942 52.147 87.1714 52.3294 86.8945L53.2795 85.3645C55.445 81.8776 58.6796 79.184 62.501 77.6857L65.3229 76.5792C65.6874 76.4348 65.9761 76.2179 66.1891 75.9291C66.1891 75.9291 66.1892 75.9291 66.1892 75.9292V75.9292Z"
        fill="white"
      ></path>
      <path
        d="M66.9301 76.5116C66.605 76.943 66.1742 77.2554 65.6699 77.4552L65.6669 77.4564L62.845 78.5629C59.2128 79.9871 56.1383 82.5473 54.08 85.8616L53.1298 87.3916L53.1232 87.4022L53.1163 87.4127C52.8322 87.8441 52.4408 88.1712 51.9782 88.4043L51.971 88.4079L51.9638 88.4114C51.4795 88.6452 50.9583 88.7581 50.4144 88.7581H50.3233H50.3094L50.2956 88.7577C49.74 88.7413 49.2131 88.601 48.7285 88.3337L48.7208 88.3295L48.7132 88.3251C48.2433 88.0544 47.8591 87.6803 47.613 87.1927L47.611 87.1889L38.47 68.8709L38.4653 68.8615L38.4654 68.8615C38.2234 68.3628 38.1545 67.8232 38.2815 67.2787L38.2853 67.2624L38.2897 67.2463C38.4394 66.6936 38.7627 66.2389 39.2067 65.8871L66.9301 76.5116ZM66.9301 76.5116C66.9362 76.5037 66.9423 76.4957 66.9484 76.4875C67.2746 76.0441 67.4525 75.5284 67.4267 74.9661C67.4012 74.3944 67.1766 73.887 66.8059 73.4648L66.799 73.457L66.7919 73.4493C66.4259 73.0507 65.9679 72.7751 65.4482 72.6103L65.4454 72.6094L42.3535 65.3653L42.3535 65.3653L42.3463 65.3631C41.8176 65.2018 41.2742 65.1612 40.7264 65.2416L40.7159 65.2432L40.7054 65.2449C40.1506 65.3392 39.6395 65.5441 39.2067 65.887L66.9301 76.5116ZM66.8633 76.5883C66.6122 76.8496 66.2617 76.9214 65.9512 76.8408C66.2634 76.9193 66.6153 76.8444 66.8633 76.5883ZM66.4863 75.0345C66.8199 75.141 67.0956 75.4271 67.1287 75.8569C67.0962 75.4349 66.8208 75.1445 66.4863 75.0348C66.4863 75.0347 66.4863 75.0346 66.4863 75.0345Z"
        stroke="#CDFFBB"
        stroke-width="1.88453"
      ></path>
    </g>
    <defs>
      <filter
        id="filter0_d_42_1241"
        x="0.177158"
        y="35.7406"
        width="113.679"
        height="39.3571"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix"></feFlood>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        ></feColorMatrix>
        <feOffset dy="2.39323"></feOffset>
        <feGaussianBlur stdDeviation="2.39323"></feGaussianBlur>
        <feComposite in2="hardAlpha" operator="out"></feComposite>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_42_1241"
        ></feBlend>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_42_1241"
          result="shape"
        ></feBlend>
      </filter>
      <filter
        id="filter1_d_42_1241"
        x="12.8813"
        y="71.5465"
        width="113.679"
        height="39.3571"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix"></feFlood>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        ></feColorMatrix>
        <feOffset dy="2.39323"></feOffset>
        <feGaussianBlur stdDeviation="2.39323"></feGaussianBlur>
        <feComposite in2="hardAlpha" operator="out"></feComposite>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_42_1241"
        ></feBlend>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_42_1241"
          result="shape"
        ></feBlend>
      </filter>
      <filter
        id="filter2_d_42_1241"
        x="12.8811"
        y="0.72957"
        width="113.679"
        height="39.3571"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix"></feFlood>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        ></feColorMatrix>
        <feOffset dy="2.39323"></feOffset>
        <feGaussianBlur stdDeviation="2.39323"></feGaussianBlur>
        <feComposite in2="hardAlpha" operator="out"></feComposite>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_42_1241"
        ></feBlend>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_42_1241"
          result="shape"
        ></feBlend>
      </filter>
      <filter
        id="filter3_dd_42_1241"
        x="32.4903"
        y="62.3748"
        width="40.6677"
        height="38.708"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix"></feFlood>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        ></feColorMatrix>
        <feOffset dy="6.59584"></feOffset>
        <feGaussianBlur stdDeviation="2.39323"></feGaussianBlur>
        <feComposite in2="hardAlpha" operator="out"></feComposite>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_42_1241"
        ></feBlend>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        ></feColorMatrix>
        <feOffset dy="0.942263"></feOffset>
        <feGaussianBlur stdDeviation="1.41339"></feGaussianBlur>
        <feComposite in2="hardAlpha" operator="out"></feComposite>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0.433854 0 0 0 0 0.875 0 0 0 0 0.575021 0 0 0 1 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="effect1_dropShadow_42_1241"
          result="effect2_dropShadow_42_1241"
        ></feBlend>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect2_dropShadow_42_1241"
          result="shape"
        ></feBlend>
      </filter>
    </defs>
  </svg>
);

const StepThreeSvg = () => (
  <svg
    width="93"
    height="128"
    viewBox="0 0 93 128"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g filter="url(#filter0_d_42_1253)">
      <rect
        width="69.375"
        height="111.478"
        rx="19.0344"
        transform="matrix(0.980553 -0.196256 0.191651 0.981463 2.05298 14.0066)"
        fill="white"
      ></rect>
    </g>
    <circle cx="47.6635" cy="62.6455" r="29.2102" fill="#A5F6FF"></circle>
    <circle
      cx="48.1346"
      cy="63.1166"
      r="18.3741"
      fill="url(#paint0_radial_42_1253)"
    ></circle>
    <path
      d="M20.1626 64.7419H68.9793C66.1287 71.037 57.078 83.9123 43.6801 85.0525C30.2823 86.1928 22.4193 71.9872 20.1626 64.7419Z"
      fill="#A5F6FF"
    ></path>
    <g filter="url(#filter1_d_42_1253)">
      <circle cx="48.3123" cy="62.6041" r="7.83918" fill="white"></circle>
      <rect
        x="51.1631"
        y="60.4658"
        width="22.8049"
        height="4.27591"
        rx="2.13796"
        fill="white"
      ></rect>
    </g>
    <defs>
      <filter
        id="filter0_d_42_1253"
        x="0.557773"
        y="1.36019"
        width="92.3812"
        height="125.875"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix"></feFlood>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        ></feColorMatrix>
        <feOffset dy="2.39323"></feOffset>
        <feGaussianBlur stdDeviation="2.39323"></feGaussianBlur>
        <feComposite in2="hardAlpha" operator="out"></feComposite>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_42_1253"
        ></feBlend>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_42_1253"
          result="shape"
        ></feBlend>
      </filter>
      <filter
        id="filter1_d_42_1253"
        x="35.6867"
        y="52.3717"
        width="43.0678"
        height="25.2514"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix"></feFlood>
        <feColorMatrix
          in="SourceAlpha"
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          result="hardAlpha"
        ></feColorMatrix>
        <feOffset dy="2.39323"></feOffset>
        <feGaussianBlur stdDeviation="2.39323"></feGaussianBlur>
        <feComposite in2="hardAlpha" operator="out"></feComposite>
        <feColorMatrix
          type="matrix"
          values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.05 0"
        ></feColorMatrix>
        <feBlend
          mode="normal"
          in2="BackgroundImageFix"
          result="effect1_dropShadow_42_1253"
        ></feBlend>
        <feBlend
          mode="normal"
          in="SourceGraphic"
          in2="effect1_dropShadow_42_1253"
          result="shape"
        ></feBlend>
      </filter>
      <radialGradient
        id="paint0_radial_42_1253"
        cx="0"
        cy="0"
        r="1"
        gradientUnits="userSpaceOnUse"
        gradientTransform="translate(61.5791 59.9795) rotate(114.386) scale(36.9037)"
      >
        <stop stop-color="#37DFF2"></stop>
        <stop offset="1" stop-color="#CDF9FE"></stop>
      </radialGradient>
    </defs>
  </svg>
);

export function HowItWorks({ mainColor }: { mainColor: string }) {
  return (
    <section className="py-20 px-4" id="how-it-works">
      <div className="md:container mx-auto md:max-w-[1005px]">
        <div className="text-center mb-16 ">
          <span
            className={cn(
              "text-[16px] md:text-lg md:text-center md:block text-left w-full font-normal flex"
            )}
            style={{ color: mainColor }}
          >
            How Famepeak works
          </span>
          <h2 className="md:text-[2.75rem] text-[32px] font-semibold text-[#2D2D2D] md:block text-left w-full md:text-center leading-tight mt-4 mb-6">
            The Famepeak Process is Simple: <br className="hidden md:block" />
            Just a Few Steps to TikTok Popularity!
          </h2>
          <p className="text-[#757575] text-lg max-w-4xl mx-auto md:text-center text-left">
            People want to follow social media accounts with quality content.
            But they often don't have the time or patience to judge an TikTok
            profile or page.
          </p>
        </div>

        <div className="flex flex-col md:flex-row items-center gap-8 mb-[1.5rem] border rounded-xl border-gray-100 px-6 py-6 max-w-[965px] mx-auto w-full">
          <div className="bg-[#ebebff] overflow-hidden shrink-0 w-16 h-16 relative rounded-full">
            <svg
              className="!w-[95px] !h-[92px] absolute -left-5 -bottom-8"
              width="168"
              height="165"
              viewBox="0 0 168 165"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M116.451 126.972C124.574 120.375 129.212 110.409 129.026 99.9458L128.088 47.1471C127.754 28.3535 112.248 13.3891 93.4542 13.723C74.6606 14.057 59.6962 29.563 60.0301 48.3565L60.196 57.6909L60.7438 127.637L87.4186 133.73C97.6203 136.06 108.327 133.568 116.451 126.972Z"
                fill="url(#paint0_linear_36_989)"
              ></path>
              <path
                d="M116.451 126.972C124.574 120.375 129.212 110.409 129.026 99.9458L128.088 47.1471C127.754 28.3535 112.248 13.3891 93.4542 13.723C74.6606 14.057 59.6962 29.563 60.0301 48.3565L60.196 57.6909L60.7438 127.637L87.4186 133.73C97.6203 136.06 108.327 133.568 116.451 126.972Z"
                fill="#D5B5FF"
              ></path>
              <path
                d="M83.6981 31.897C85.8877 36.3309 91.0796 35.499 92.5268 31.8607M96.9776 31.584C99.1672 36.0179 104.359 35.1859 105.806 31.5477M81.3871 40.4934C90.8794 51.1275 101.498 48.8117 107.903 37.7018"
                stroke="#6635A6"
                stroke-width="1.85102"
                stroke-linecap="round"
                stroke-linejoin="round"
              ></path>
              <rect
                x="141.999"
                y="31.8115"
                width="19.9449"
                height="54.9314"
                rx="9.97243"
                transform="rotate(39.4683 141.999 31.8115)"
                fill="#D5B5FF"
              ></rect>
              <rect
                width="19.9449"
                height="54.9314"
                rx="9.97243"
                transform="matrix(-0.771976 0.635651 0.635651 0.771976 44.9761 31.8115)"
                fill="#D5B5FF"
              ></rect>
            </svg>
          </div>
          <p className=" text-[#757575] text-[15px]">
            Instead, they rely on an account's amount of likes, or their number
            of views and followers, to decide whether to click the "follow"
            button.
          </p>
          <p className=" text-[#757575] text-[15px]">
            Famepeak's{" "}
            <span className="text-[#9750DD] font-medium">
              high-quality service
            </span>{" "}
            takes advantage of the TikTok algorithm to make your account
            popular in a flash!
          </p>
        </div>

        <div className="flex flex-col md:flex-row justify-center items-center gap-6">
          <StepCard
            number="1"
            title="Pick a package"
            description="Choose the TikTok likes, followers, shares, or views package that you're interested in. Feel free to get in touch if you have an inquiry about a bulk order."
            variant="purple"
            iconPosition="-18px"
          />
          <StepCard
            number="2"
            title="Fill the info"
            description="We will never ask you for sensitive details such as your password. All you have to do is provide us with your TikTok username and leave the rest to us!"
            variant="green"
            iconPosition="-0px"
          />
          <StepCard
            number="3"
            title="See the growth"
            description="All orders start within a few minutes, so you'll see growth in your TikTok account very quickly. We've made the process super easy."
            variant="blue"
            iconPosition="-21px"
          />
        </div>
      </div>
    </section>
  );
}
